import { Component, inject, Input, OnInit } from '@angular/core';
import { getPaaMilestoneFormObject } from './utils/milestone-form-object';
import { RefParamsService } from '@/pages/settings/ref-params/ref-params.service';
import { Currency, RefTypeEnum } from '@/types';
import { BehaviorSubject, catchError, filter, forkJoin, Observable, of, switchMap } from 'rxjs';
import { CommonModule } from '@angular/common';
import { FormBuilderComponent } from '@/components/form-builder/form-builder.component';
import { FileService, NAMESPACE_ENUM } from '@/components/shared/file-list/file-list.service';
import { SpinnerComponent } from '@/components/shared/spinner/spinner.component';
import { DynamicForm, FileRepresentation, SelectOption } from '@/components/form-builder/form-builder.types';
import { getFormBuilderObjectFactory } from '@/components/form-builder/utils/form-builder-factory';
import { PaaMilestoneRepository, PaaMilestonesRepositoryFactory, PaaMileStoneStore } from '@/store/paa-state/milestones-state/milestones.repository';
import { PassingModeService } from '@/pages/settings/passing-mode/passing-mode.service';
import { CurrencyService } from '@/pages/settings/currency/currency.service';
import { FundingSourceService } from '@/pages/settings/funding-sources/funding-sources.service';

@Component({
  selector: 'sm-paa-milestone-form',
  imports: [FormBuilderComponent, CommonModule, SpinnerComponent],
  templateUrl: './milestone-form.component.html',
  styleUrl: './milestone-form.component.scss',
})
export class PaaMilestoneFormComponent implements OnInit {
  //services
  fundingSourceService = inject(FundingSourceService);
  refParamsService = inject(RefParamsService);
  passingModeService = inject(PassingModeService);
  currencyService = inject(CurrencyService);
  fileService = inject(FileService);

  //inputs
  @Input() store?: PaaMileStoneStore;
  @Input() isDisabled?: Observable<boolean>;
  @Input() acId?: Observable<number>;
  @Input() minUpdateDate?: Date;

  loading$ = new BehaviorSubject<boolean>(true);

  milestoneRepository?: PaaMilestoneRepository;

  marketTypesOptions: SelectOption[] = [];
  financialSourcesOptions: SelectOption[] = [];
  currencyOptions: SelectOption[] = [];
  selectionModeOptions: SelectOption[] = [];
  budgetAllocationOptions: SelectOption[] = [];
  currencies: Currency[] = [];

  //form-object
  formObject: DynamicForm = getFormBuilderObjectFactory();

  ngOnInit() {
    this.loadFormOptions();
  }

  private loadFormOptions() {
    this.loading$.next(true);
    forkJoin({
      marketTypes: this.refParamsService.getSpecializedRefParams(RefTypeEnum.MARKET_TYPE),
      financialSources: this.fundingSourceService.getAll(),
      budgetallocation: this.refParamsService.getSpecializedRefParams(RefTypeEnum.BUDGET_ALLOCATION),
      currencies: this.currencyService.getAll(),
    }).subscribe(({ marketTypes, financialSources, budgetallocation, currencies }) => {
      this.marketTypesOptions = marketTypes.map(ref => ({ name: ref.description, code: ref.id }));
      this.financialSourcesOptions = financialSources.map(ref => ({ name: ref.description, code: ref.id }));
      this.currencyOptions = currencies.map(curr => ({ name: curr.description, code: curr.id }));
      this.budgetAllocationOptions = budgetallocation.map(ref => ({ name: ref.description, code: ref.id }));
      this.currencies = currencies;

      this.buildFormObject();
      this.setupSelectionModeLoader();

      this.loading$.next(false);
    });
  }

  private setupSelectionModeLoader() {
    const typeMarcher$ = this.milestoneRepository?.getNestedObservable<number>('milestoneDto', 'typeMarcher.id', false);
    if (!typeMarcher$) return;

    this.loading$.next(true);

    typeMarcher$
      .pipe(
        filter((id): id is number => !!id),
        switchMap(typeMarcherId => this.passingModeService.getByMarketType(typeMarcherId).pipe(catchError(() => of([]))))
      )
      .subscribe(selectionModes => {
        this.selectionModeOptions = selectionModes.map(ref => ({
          name: ref.libelle,
          code: ref.id,
        }));
        this.buildFormObject();
        this.loading$.next(false);
      });
  }

  private buildFormObject() {
    if (this.store) {
      this.milestoneRepository = PaaMilestonesRepositoryFactory.create(this.store);
      this.formObject = getPaaMilestoneFormObject({
        store: this.milestoneRepository,
        download: (file: FileRepresentation) => this.fileService.download(file, NAMESPACE_ENUM.PAA),
        openFile: (file: FileRepresentation) => this.fileService.openFile(file, NAMESPACE_ENUM.PAA),
        marketTypesOptions: this.marketTypesOptions,
        financialSourcesOptions: this.financialSourcesOptions,
        currencyOptions: this.currencyOptions,
        selectionModeOptions: this.selectionModeOptions,
        budgetAllocationOptions: this.budgetAllocationOptions,
        isDisabled: this.isDisabled,
        minDate: this.minUpdateDate,
        currencies: this.currencies,
      });
    }
  }
}
